import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  getCardSignals,
  getCardData,
  getCardConfig,
  changeCardVariant,
} from "../store/collection-store";

/**
 * Variant dropdown selector for collection product cards.
 * Replaces the legacy variant-dropdown-cp component with a reactive Lit implementation.
 *
 * Usage: <card-variant-selector data-card-id="p-12345"></card-variant-selector>
 */
@customElement("card-variant-selector")
export class CardVariantSelector extends LitElement {
  @property({ type: String, attribute: "data-card-id" }) cardId = "";

  @state() private _selectedId: number | null = null;
  @state() private _variants: any[] = [];

  private _disposeEffect?: () => void;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.cardId) return;

    // Load product data and filter variants
    const productData = getCardData(this.cardId);
    const config = getCardConfig(this.cardId);

    if (!productData || !config) {
      console.warn(`card-variant-selector: no data/config for ${this.cardId}`);
      return;
    }

    // Filter out hidden variants
    const allVariants = productData.variants ?? [];
    this._variants = allVariants.filter(
      (v: any) => !config.hiddenVariantIds.has(v.id)
    );

    // Subscribe to the selected variant signal from the collection store.
    // Must run here (not in the constructor) — the `data-card-id` attribute is
    // not reflected onto `this.cardId` until after construction, so a
    // constructor effect would guard-return before reading any signal and never
    // subscribe, leaving the native <select> stuck on its first <option>.
    this._disposeEffect = effect(() => {
      const signals = getCardSignals(this.cardId);
      if (!signals) return;
      this._selectedId = signals.selectedVariantId.value ?? null;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeEffect?.();
  }

  private _onVariantChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    const variantId = parseInt(select.value, 10);
    if (!isNaN(variantId)) {
      changeCardVariant(this.cardId, variantId);
    }
  }

  render() {
    // Don't render if there's only one variant or no variants
    if (this._variants.length <= 1) {
      return html`<div class="variant-select-placeholder"></div>`;
    }

    return html`
      <div class="product-form__input product-form__input--dropdown w-full h-[42px] rounded-sm border border-[var(--color-neutral-300)] bg-white transition-colors duration-200 focus-within:border-[var(--color-secondary-500)] focus-within:ring-2 focus-within:ring-[var(--color-secondary-200)]">
        <div class="select relative h-full">
          <select
            @change=${this._onVariantChange}
            .value=${this._selectedId != null ? String(this._selectedId) : ""}
            class="peer select__select h-full w-full appearance-none pr-12 pl-3 text-xs font-medium uppercase leading-[18px] text-[var(--color-neutral-700)] outline-none tracking-wider"
            aria-label="Select variant"
          >
            ${this._variants.map(
              (v: any) => html`
                <option
                  value="${v.id}"
                  ?selected=${this._selectedId === v.id}
                >
                  ${v.title}
                </option>
              `
            )}
          </select>
          <svg
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 10 6"
            class="pointer-events-none absolute right-4 top-1/2 h-[8px] w-[14px] -translate-y-1/2 text-[var(--color-neutral-700)] transition-colors duration-200 peer-focus:text-[var(--color-secondary-500)]"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>
    `;
  }
}
